import {
  Box,
  Card,
  CardContent,
  InputAdornment,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import { ClipboardList, Search } from "lucide-react"
import { useCallback, useEffect, useRef, useState, type JSX } from "react"
import { useNavigate } from "react-router-dom"

import { EmptyState } from "@/components/ui/empty-state"
import { Pagination, paginationPropsFromMeta } from "@/components/ui/pagination"
import {
  PRE_SHIP_PER_PAGE,
  preShipApi,
  type PreShipListParams,
  type PreShipPagination,
  type PreShipSummary,
} from "@crm-src/lib/pre-ship-api"

export const PRE_SHIP_SEARCH_DEBOUNCE_MS = 300

export interface PreShipListProps {
  /** Narrow to one customer (the customer profile's tab); omit for every customer the viewer can see. */
  customerId?: string
  refreshKey?: number
  emptyMessage?: string
}

function formatDate(value: string | null): string {
  if (!value) return "—"
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
}

/**
 * ONE pre-ship questionnaire table — CRM ▸ Pre-ship Questionnaires and the
 * Pre-ship tab of a customer profile both render this, differing only in the
 * `customerId` sent to crm-api. Search and the page are asked of the SERVER,
 * which scopes the rows to the viewer. There is no status (CEO, 2026-10-03).
 */
export function PreShipList({ customerId, refreshKey = 0, emptyMessage = "No pre-ship questionnaires yet." }: PreShipListProps): JSX.Element {
  const navigate = useNavigate()
  const showCustomer = customerId === undefined

  const [input, setInput] = useState("")
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const [rows, setRows] = useState<PreShipSummary[]>([])
  const [pagination, setPagination] = useState<PreShipPagination | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const next = input.trim()
    if (next === search) return
    const handle = setTimeout(() => {
      setSearch(next)
      setPage(1)
    }, PRE_SHIP_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [input, search])

  // Drop a slow earlier response that lands after a faster later one.
  const requestSeq = useRef(0)

  const load = useCallback(async () => {
    const seq = ++requestSeq.current
    const params: PreShipListParams = {
      page,
      per_page: PRE_SHIP_PER_PAGE,
      ...(search ? { q: search } : {}),
      ...(customerId !== undefined ? { customer_id: customerId } : {}),
    }
    setLoading(true)
    try {
      const response = await preShipApi.list(params)
      if (seq !== requestSeq.current) return
      setRows(response.pre_ship_questionnaires ?? [])
      setPagination(response.meta?.pagination ?? null)
      setError(null)
    } catch (err: any) {
      if (seq !== requestSeq.current) return
      setRows([])
      setError(err?.response?.data?.error || "The questionnaires could not be loaded.")
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [page, search, customerId])

  useEffect(() => {
    void load()
  }, [load, refreshKey])

  const filtered = !!search
  const columnCount = 4 + (showCustomer ? 1 : 0)

  return (
    <Card data-cy="preship-list">
      <CardContent sx={{ display: "grid", gap: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder={showCustomer ? "Search title or customer..." : "Search title..."}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            inputProps={{ "data-cy": "preship-search", "aria-label": "Search questionnaires" }}
            sx={{ width: { xs: "100%", sm: 320 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ display: "flex", color: "text.disabled" }}>
                    <Search size={16} />
                  </Box>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Questionnaire</TableCell>
                {showCustomer && <TableCell>Customer</TableCell>}
                <TableCell sx={{ minWidth: 150 }}>Filled in</TableCell>
                <TableCell>Last edited</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((q) => {
                const pct = q.progress.total > 0 ? Math.round((q.progress.answered / q.progress.total) * 100) : 0
                return (
                  <TableRow key={q.id} hover data-cy="preship-row" onClick={() => navigate(`/crm/pre-ship/${q.id}`)} sx={{ cursor: "pointer" }}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>{q.title}</Typography>
                    </TableCell>
                    {showCustomer && <TableCell sx={{ maxWidth: 240 }}>{q.customer_name || "—"}</TableCell>}
                    <TableCell>
                      <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
                        {q.progress.answered} of {q.progress.total}
                      </Typography>
                      <LinearProgress variant="determinate" value={pct} sx={{ height: 5, borderRadius: 3 }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: "text.secondary" }}>
                      {q.updated_by_kind === "customer" ? `Customer · ${q.updated_by_email ?? ""}` : q.updated_by_email || "—"}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(q.updated_at)}</TableCell>
                  </TableRow>
                )
              })}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columnCount} sx={{ border: 0, pt: 2 }}>
                    <EmptyState
                      icon={<ClipboardList size={20} />}
                      title={error ? "Couldn't load questionnaires" : filtered ? "No questionnaire matches" : emptyMessage}
                      {...(error ? { description: error } : filtered ? { description: "Try a different search." } : {})}
                    />
                  </TableCell>
                </TableRow>
              )}
              {loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columnCount} sx={{ color: "text.secondary" }}>
                    Loading questionnaires...
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        <Pagination {...paginationPropsFromMeta(pagination)} entityNoun="questionnaires" hideIfSinglePage onPageChange={setPage} />
      </CardContent>
    </Card>
  )
}
